import { columnsDataComplex } from "./variables/columnsData";
import { useAuth } from "context/AuthProvider";
import { useEffect, useState, useRef } from "react"; // Import useEffect and useState
import { Radio, Segmented, Spin, Tag, Tooltip } from "antd";
import Card from "components/card";
import CommonTable from "components/common/CommonTable";
import ChatbotWidget from "components/ChatbotWidget";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { MdAdd, MdClose } from "react-icons/md";
import { useDisclosure } from "@chakra-ui/hooks";
import { urls } from "helpers/urls";
import CommonNotification from "components/common/CommonNotification";
import Request from "helpers/request";
import { TbCircleCheck, TbCircleX, TbRefresh, TbTrash } from "react-icons/tb";
import { ClipLoader } from "react-spinners";
import { IoMdAdd, IoMdRefresh } from "react-icons/io";

const DataSource = ({ id, name, refreshTrigger }) => {
  const { currentSub, plan } = useAuth();
  const [currentSubWithPlanName, setCurrentSubWithPlanName] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCrawling, setIsCrawling] = useState(false);
  const [modalType, setModalType] = useState("crawl"); // 'crawl' or 'singleUrl'
  const [isOpen, setIsOpen] = useState(true);
  const [site_url, setSiteUrl] = useState("");
  const [limit, setLimit] = useState("50");
  const [activeTab, setActiveTab] = useState("Websites");
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  const messagesEndRef = useRef(null);
  const [pageLoader, setPageLoader] = useState(false);
  const [deletingUrl, setDeletingUrl] = useState(null);
  const [messages, setMessages] = useState([]);
  const [disabled, setDisabled] = useState("");
  const [isAddNewDisable, setIsAddNewDisable] = useState(false);
  const [updaterecrawling, setUpdaterecrawling] = useState(false);
  // Appearance data state
  const [appearanceData, setAppearanceData] = useState(null);
  const appearanceUrl = urls.getAppearanceUrl(id);

  // Function to fetch appearance data
  const fetchAppearance = async () => {
    try {
      const response = await Request.get(appearanceUrl);
      setAppearanceData(response?.data?.data);
      // console.log("Appearance data refreshed:", response?.data?.data);
    } catch (error) {
      console.error("Failed to fetch appearance data:", error);
    }
  };

  // Fetch appearance data on component mount
  useEffect(() => {
    fetchAppearance();
  }, [appearanceUrl]);

  // Refetch appearance data when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchAppearance();
    }
  }, [refreshTrigger]);

  ////////////////////////////////////////////////////////////////
  // SESSION ID MANAGEMENT
  ////////////////////////////////////////////////////////////////

  const [sessionId, setSessionId] = useState(null);

  // Poll for session ID from localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const stored = localStorage.getItem("chat_session_id");
      if (!stored) return;

      const allSessionIds = JSON.parse(stored);
      const currentSessionId = allSessionIds?.[id];

      setSessionId(currentSessionId);
    }, 100);

    return () => clearInterval(interval);
  }, [id]);

  ////////////////////////////////////////////////////////////////
  // CHAT FUNCTIONALITY
  ////////////////////////////////////////////////////////////////

  const handleSendMessage = async (messageText, animateTyping = null) => {
    if (!messageText.trim() || isLoading) return;

    setIsLoading(true);

    const userMessage = {
      id: `user_${Date.now()}_${Math.random()}`,
      text: messageText,
      type: "user",
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    try {
      const response = await Request.postJson(urls.newChat, {
        site_id: id,
        query: messageText,
        session_id: sessionId,
      });

      const apiData = response?.data?.data;
      const botAnswer =
        apiData?.answer || "I couldn't process your request. Please try again.";
      const sources = apiData?.sources || [];
      // FIX: Use message_id instead of assistant_message_id
      const messageId = apiData?.message_id;

      setIsLoading(false);

      if (animateTyping) {
        await animateTyping(botAnswer, messageId, sources);
        const botMessage = {
          // FIX: Use the correct message_id from API response
          id: messageId || `agent_${Date.now()}_${Math.random()}`,
          text: botAnswer,
          type: "agent",
          timestamp: Date.now(),
          sources: sources,
        };
        setMessages((prev) => [...prev, botMessage]);
      } else {
        const botMessage = {
          // FIX: Use the correct message_id from API response
          id: messageId || `agent_${Date.now()}_${Math.random()}`,
          text: botAnswer,
          type: "agent",
          timestamp: Date.now(),
          sources: sources,
        };
        setMessages((prev) => [...prev, botMessage]);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setIsLoading(false);
      CommonNotification.error("Something went wrong. Please try again.");

      const errorMessage = {
        id: `error_${Date.now()}_${Math.random()}`,
        text: "Sorry, I encountered an error while processing your message. Please try again.",
        type: "agent",
        timestamp: Date.now(),
        sources: [],
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };
  const handleStartTyping = () => {
    // console.log("Bot started typing...");
  };

  const handleFinishTyping = () => {
    // console.log("Bot finished typing...");
  };

  ////////////////////////////////////////////////////////////////
  // OTHER COMPONENT LOGIC
  ////////////////////////////////////////////////////////////////

  useEffect(() => {
    const planIdToPlanData = {};
    plan.forEach((planItem) => {
      planIdToPlanData[planItem.id] = planItem;
    });
  }, [currentSub, plan]);

  const startCrawl = () => {
    setIsCrawling(true);
    Request.postJson(urls.startCrawling, {}, { site_id: id, site_url, limit })
      .then((response) => {
        console.log("response", response);
        CommonNotification.success(
          "Crawling process has been initiated successfully."
        );
        onModalClose();
        getcrawl();
      })
      .catch((error) => {
        CommonNotification.error(
          error?.response?.data?.message || "Error starting crawl"
        );
      })
      .finally(() => {
        setIsCrawling(false);
      });
  };

  const startRecrawl = () => {
    setPageLoader(true);
    Request.postJson(urls.startRecrawl, {}, { site_id: id })
      .then((response) => {
        CommonNotification.success(
          "Recrawl process has been initiated successfully."
        );
        getcrawl();
      })
      .catch((error) => {
        CommonNotification.error(
          error?.response?.data?.message || "Error starting recrawl"
        );
      })
      .finally(() => {
        setPageLoader(false);
      });
  };

  const getcrawl = (isFirstCall = false) => {
    try {
      setPageLoader(true);
      Request.get(urls.getcrawl + `/${id}`)
        .then((response) => {
          console.log("response", response);
          const recentCrawls =
            response.data?.data?.crawl_status?.recent_crawls || [];
          const sortedCrawls = [...recentCrawls].sort((a, b) => {
            if (
              a.crawled_status === "completed" &&
              b.crawled_status !== "completed"
            ) {
              return -1;
            }
            if (
              a.crawled_status !== "completed" &&
              b.crawled_status === "completed"
            ) {
              return 1;
            }
            return 0;
          });
          setCurrentSubWithPlanName(sortedCrawls);
          // setDisabled(
          //   response.data?.data?.crawl_status?.status !== "active" ||
          //     response.data?.data?.crawl_status?.recent_crawls?.length === 0
          // );

          if (!isFirstCall) {
            setIsAddNewDisable(true);
          }
        })
        .catch((error) => {
          CommonNotification.error(
            error?.response?.data?.message || "Error starting crawl"
          );
        })
        .finally(() => {
          setPageLoader(false);
        });
    } catch (error) {
      CommonNotification.error(
        error?.response?.data?.message || "Error starting crawl"
      );
    }
  };

  useEffect(() => {
    getcrawl(true);
  }, []);

  const deleteurl = (item, site_id) => {
    setDeletingUrl(item);
    Request.postJson(`${urls.deleteurl}`, { url_id: item, site_id })
      .then((response) => {
        CommonNotification.success("Site deleted successfully");
        getcrawl();
      })
      .catch((error) => {
        CommonNotification.error("Error deleting site");
      })
      .finally(() => {
        setDeletingUrl(null);
      });
  };

  const updateRecrawlurl = (item, site_id) => {
    setUpdaterecrawling(item);
    Request.putJson(urls.updateRecrawlurl, {}, { url_id: item, site_id })
      .then((response) => {
        CommonNotification.success("URL recrawled successfully");
        getcrawl();
      })
      .catch((error) => {
        CommonNotification.error("Error recrawling URL");
      })
      .finally(() => {
        setUpdaterecrawling(null);
      });
  };

  const handleAddSingleUrl = () => {
    setIsCrawling(true);
    Request.postJson(`${urls.addNewUrl}/${id}?url=${encodeURIComponent(site_url)}`, {})
      .then((response) => {
        CommonNotification.success("URL added successfully.");
        onModalClose();
        getcrawl();
      })
      .catch((error) => {
        CommonNotification.error(
          error?.response?.data?.message || "Error adding URL"
        );
      })
      .finally(() => {
        setIsCrawling(false);
      });
  };

  const columnsDataComplex = [
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      render: (url, record) =>
        record?.is_active ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className=" hover:text-customBlue"
          >
            {url}
          </a>
        ) : (
          <span className="text-gray-500">{url}</span>
        ),
      sorter: (a, b) => a.url.localeCompare(b.url),
      width: 50,
    },
    {
      title: "Web Page",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "crawled_status",
      key: "crawled_status",
      sorter: (a, b) => a.crawled_status.localeCompare(b.crawled_status),
      render: (status) => {
        const style =
          status === "completed"
            ? { backgroundColor: "#dae7fd", color: "#3b79dd" }
            : status === "pending"
            ? { backgroundColor: "#fff3e1", color: "#8c621e" }
            : { color: "inherit" };
        return (
          <span
            key={status}
            style={style}
            className="rounded-[50px] px-[15px] py-[4px] text-[14px] font-[500]"
          >
            {status}
          </span>
        );
      },
      width: 90,
    },
    {
      title: "Ready to chat",
      dataIndex: "vector_status",
      key: "vector_status",
      width: 120,
      render: (vector_status) =>
        vector_status ? (
          <Tooltip title="Ready to chat">
            <TbCircleCheck style={{ color: "green", fontSize: "20px" }} />
          </Tooltip>
        ) : (
          <Tooltip title="Not Ready">
            <TbCircleX style={{ color: "red", fontSize: "20px" }} />
          </Tooltip>
        ),
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "id",
      width: 120,
      render: (item, record) => (
        <div className="flex items-center gap-2">
          <Tooltip title="Recrawl">
            <div>
              {updaterecrawling === item ? (
                <ClipLoader color={"customBlue"} size={18} />
              ) : (
                <button
                  className="text-[18px] text-customBlue hover:text-customBlue/70"
                  onClick={() => updateRecrawlurl(item, id)}
                >
                  <TbRefresh />
                </button>
              )}
              {console.log(updaterecrawling)}
            </div>
          </Tooltip>
          <Tooltip title="Delete">
            <div className="relative">
              {deletingUrl === item ? (
                <ClipLoader color={"#ef4444"} size={18} />
              ) : (
                record?.is_active && (
                  <button
                    className="text-[18px] text-[red] hover:text-[red]/70 disabled:opacity-50"
                    onClick={() => deleteurl(item, id)}
                  >
                    <TbTrash />
                  </button>
                )
              )}
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  const items = [
    {
      key: "1",
      label: "Websites",
      children: (
        <Card
          extra={
            "w-full h-[auto] sm:overflow-x-auto rounded-none rounded-b-md rounded-tr-md"
          }
        >
          <div className="">
            <div className="">
              <div
                className="mt-[20px] flex items-center justify-between gap-2"
                style={{ fontFamily: "figtree" }}
              >
                <div className="flex items-center gap-2">
                <div>
                  <button
                    className="mb-2 flex items-center gap-1 rounded-md bg-customBlue px-3 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => {
                      setModalType("crawl");
                      onModalOpen();
                    }}
                    // disabled={isAddNewDisable}
                  >
                    <MdAdd className="text-[16px] text-[#fff]" />
                    Add New
                  </button>
                </div>
                <div>
                <button
                    className="mb-2 flex items-center gap-1 rounded-md bg-customBlue px-3 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => {
                      setModalType("singleUrl");
                      onModalOpen();
                    }}
                    // disabled={isAddNewDisable}
                  >
                    <MdAdd className="text-[16px] text-[#fff]" />
                    Add Single URL
                  </button>
                </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="mb-2 flex items-center gap-1 rounded-md bg-customBlue px-3 py-2 text-white ms-2 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={startRecrawl}
                    // disabled={disabled}
                  >
                    <TbRefresh className="text-[16px] text-[#fff]" />
                    Complete Recrawl
                  </button>
                  <button
                    className="mb-2 flex items-center gap-1 rounded-md bg-customBlue px-3 py-2 text-white ms-2 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={getcrawl}
                    // disabled={!isAddNewDisable}
                  >
                    <IoMdRefresh className="text-[16px] text-[#fff]" />
                    Refresh
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <Spin spinning={pageLoader}>
                  <CommonTable
                    site_id={id}
                    columns={columnsDataComplex}
                    dataSource={currentSubWithPlanName}
                    rowClassName={(record) =>
                      record?.is_active ? "" : "opacity-50 cursor-not-allowed"
                    }
                  />
                </Spin>
              </div>
            </div>
          </div>
        </Card>
      ),
    },
    {
      key: "2",
      label: "FAQs",
      children: (
        <Card
          extra={
            "w-full h-auto p-4 sm:overflow-x-auto rounded-none rounded-b-md rounded-tr-md"
          }
        >
          <div className="flex h-[60vh] items-center justify-center overflow-x-scroll xl:overflow-hidden">
            <div className="text-black text-3xl font-bold dark:text-white">
              Coming Soon
            </div>
          </div>
        </Card>
      ),
    },
    {
      key: "3",
      label: "API",
      children: (
        <Card
          extra={
            "w-full h-auto p-4 sm:overflow-x-auto rounded-none rounded-b-md rounded-tr-md"
          }
        >
          <div className="flex h-[60vh] items-center justify-center overflow-x-scroll xl:overflow-hidden">
            <div className="text-black text-3xl font-bold dark:text-white">
              Coming Soon
            </div>
          </div>
        </Card>
      ),
    },
  ];

  // Updated function to fetch chat messages with new API response format
  const fetchChatMessages = async (sessionId) => {
    if (!sessionId || !id) return;

    try {
      const response = await Request.get(urls.getChatMessages(sessionId), {
        site_id: id,
      });

      if (response.data && response.data.success) {
        const fetchedMessages = response.data.data.messages || [];

        // Transform API messages to your component's message format
        const transformedMessages = [];

        fetchedMessages.forEach((msg, index) => {
          // Add user message
          const userMessage = {
            id: `user_${msg.id}_${index}`,
            text: msg.user_query,
            type: "user",
            timestamp: new Date(msg.created_at).getTime() || Date.now(),
            sources: [],
          };
          transformedMessages.push(userMessage);

          // Add assistant message
          const assistantMessage = {
            id: msg.id,
            text: msg.assistant_response,
            type: "agent",
            timestamp: new Date(msg.created_at).getTime() || Date.now(),
            sources: msg.sources || [],
            rating: msg.rating,
          };
          transformedMessages.push(assistantMessage);
        });

        setMessages(transformedMessages);

        // NEW: Initialize message states with fetched ratings
        const initialMessageStates = {};
        transformedMessages.forEach((message, index) => {
          if (message.type === "agent" && message.rating !== undefined) {
            initialMessageStates[index] = {
              liked: message.rating === 1,
              disliked: message.rating === -1,
              copied: false,
            };
          } else {
            initialMessageStates[index] = {
              liked: false,
              disliked: false,
              copied: false,
            };
          }
        });
      }
    } catch (error) {
      console.error("Failed to fetch chat messages:", error);
      CommonNotification.error("Failed to load chat history");
    }
  };

  // Updated session ID effect remains the same
  useEffect(() => {
    const interval = setInterval(() => {
      const stored = localStorage.getItem("chat_session_id");
      if (!stored) return;

      const allSessionIds = JSON.parse(stored);
      const currentSessionId = allSessionIds?.[id];

      if (currentSessionId && currentSessionId !== sessionId) {
        setSessionId(currentSessionId);
        // Load chat history when session ID is available
        fetchChatMessages(currentSessionId);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [id, sessionId]);

  return (
    <div className="font-sans">
      {pageLoader && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-white bg-opacity-75">
          <ClipLoader size={50} color={"#123abc"} loading={pageLoader} />
        </div>
      )}
      <div
        className="inner-source-tabs-wrap"
        style={{ display: "flex", gap: "50px", width: "100%" }}
      >
        <div style={{ width: "65%" }}>
          <div className="mb-3">
            <Segmented
              size="large"
              options={items.map((it) => ({
                label: it.label,
                value: it.label,
              }))}
              value={activeTab}
              onChange={setActiveTab}
            />
          </div>
          {items.find((it) => it.label === activeTab)?.children}
        </div>
        <div className="w-[35%] ">
          <ChatbotWidget
            bot_type="admin_widget"
            input={input}
            setInput={setInput}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            messagesEndRef={messagesEndRef}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            liked={liked}
            setLiked={setLiked}
            disliked={disliked}
            setDisliked={setDisliked}
            copiedMsg={copiedMsg}
            setCopiedMsg={setCopiedMsg}
            messages={messages}
            onSendMessage={handleSendMessage}
            onStartTyping={handleStartTyping}
            onFinishTyping={handleFinishTyping}
            sessionId={sessionId}
            siteId={id}
            {...(appearanceData && {
              widgetPosition: appearanceData.position,
              headerColor: appearanceData.header_color,
              agentMessageColor: appearanceData.agent_message_color,
              agentTextColor: appearanceData.agent_text_color,
              widgetIconColor: appearanceData.icon_color,
              widgetIcon: appearanceData.icon,
            })}
          />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={onModalClose}
        className="!z-[1010]"
        motionPreset="scale"
      >
        <ModalOverlay className="bg-[#000] !opacity-40 backdrop-blur-xl" />
        <ModalContent className="top-[15vh] !z-[1002] !m-auto !max-w-[50%]">
          <ModalBody>
            <MdClose
              onClick={onModalClose}
              className="absolute top-3 right-3 z-10 h-5 w-5 cursor-pointer rounded-md bg-[#000]/25 text-white"
            />
            <div className="rounded-lg bg-white p-8">
              <h3 className="mb-5 text-2xl font-[600]">
                {modalType === "crawl" ? "Add Website Base URL" : "Add Single URL"}
              </h3>
              <div className="flex flex-col items-start justify-start gap-2">
                <div className="mb-3 w-full">
                  <label htmlFor="">URL</label>
                  <input
                    className="w-full rounded-md border border-customBlue px-4 py-2 text-customBlue hover:text-brand-700"
                    type="text"
                    placeholder="Enter URL"
                    value={site_url}
                    onChange={(e) => setSiteUrl(e.target.value)}
                  />
                </div>

                {modalType === "crawl" && (
                  <div>
                    <label htmlFor="">Limit</label>
                    <Radio.Group
                      block
                      options={[
                        { label: "50", value: "50" },
                        { label: "100", value: "100" },
                        { label: "150", value: "150" },
                        { label: "200", value: "200" },
                        {
                          label: "All",
                          value: "-1",
                        }
                      ]}
                      defaultValue="50"
                      onChange={(e) => setLimit(e.target.value)}
                      value={limit}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-center">
                <button
                  className="rounded-md bg-customBlue px-4 py-2 text-center text-white disabled:opacity-50"
                  onClick={modalType === "crawl" ? startCrawl : handleAddSingleUrl}
                  disabled={
                    site_url.trim() === "" ||
                    (modalType === "crawl" && limit.trim() === "")
                  }
                >
                  Add
                </button>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default DataSource;
